import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
export type Lang = 'es' | 'en';
type Copy = {
  nav: {
    experience: string;
    services: string;
    houseCall: string;
    hours: string;
    book: string;
    bookShort: string;
  };
  hero: {
    eyebrow: string;
    quote: string;
    ctaPrimary: string;
    ctaSecondary: string;
  };
  truth: {
    title: string;
    lines: string[];
    kicker: string;
  };
  nextLevel: {
    eyebrow: string;
    title: string;
    body: string;
    pillars: string[];
    lead: string;
    punch: string;
  };
  trust: {
    eyebrow: string;
    headline: string;
    imageAlt: string;
  };
  services: {
    eyebrow: string;
    title: string;
    book: string;
  };
  houseCall: {
    title: string;
    subtitle: string;
    places: string[];
    punch: string;
  };
  hours: {
    eyebrow: string;
    title: string;
    rows: {
      day: string;
      time: string;
    }[];
  };
  guarantee: {
    title: string;
    body: string;
    kicker: string;
  };
  booking: {
    title: string;
    address: string;
    phone: string;
    instagram: string;
    cta: string;
  };
  footer: {
    tagline: string;
    quote: string;
  };
  langLabel: string;
};
const es: Copy = {
  nav: {
    experience: 'Experiencia',
    services: 'Servicios',
    houseCall: 'House Call',
    hours: 'Horario',
    book: 'Reserva',
    bookShort: 'Reservar'
  },
  hero: {
    eyebrow: 'Exclusive Barbershop · Marbella',
    quote: '«Tu imagen habla antes de que abras la boca. Asegúrate de que diga lo correcto.»',
    ctaPrimary: 'Reserva tu experiencia',
    ctaSecondary: 'Llamar ahora'
  },
  truth: {
    title: 'La verdad que nadie te ha dicho',
    lines: ['Has pagado bien. Has probado varios sitios. Y sigues saliendo con algo que es correcto — pero nunca perfecto.', 'El problema no son los barberos. El problema es que ningún lugar fue diseñado para hombres con tus estándares.'],
    kicker: 'HASTA AHORA.'
  },
  nextLevel: {
    eyebrow: 'Otro nivel',
    title: 'Bienvenido a otro nivel',
    body: 'DuvayBarberVIP no es una barbería más en Marbella. Es el único espacio donde la técnica de precisión internacional, la atención absoluta y un entorno de lujo real se fusionan en una sola experiencia.',
    pillars: ['Sin esperas', 'Sin improvisaciones'],
    lead: 'Cada visita es una sesión diseñada para una sola cosa:',
    punch: 'Que salgas sintiéndote invencible.'
  },
  trust: {
    eyebrow: 'Confianza',
    headline: 'SI ELLOS CONFIARON, TU PODRIAS SER EL PROXIMO',
    imageAlt: 'Cliente profesional en DuvayBarberVIP con camiseta firmada'
  },
  services: {
    eyebrow: 'El menú',
    title: 'Lo que ofrecemos',
    book: 'Reservar'
  },
  houseCall: {
    title: 'House Call Service',
    subtitle: 'Barbers privados y atención exclusiva',
    places: ['Villas', 'Hoteles', 'Yates', 'Eventos privados'],
    punch: 'El mismo nivel. En tu terreno.',
    videoText: 'Lujo y experiencia vip'
  },
  hours: {
    eyebrow: 'Horario',
    title: 'Cuándo estamos',
    rows: [{
      day: 'Lun – Vie',
      time: '10:00–14:00 · 16:00–21:00'
    }, {
      day: 'Sábado',
      time: '10:00–14:00 · 15:00–18:00'
    }, {
      day: 'Domingo',
      time: 'Cerrado'
    }]
  },
  guarantee: {
    title: 'Una sola garantía',
    body: 'Si el resultado no es exactamente lo que esperabas, lo corregimos. Sin preguntas. Sin excusas.',
    kicker: 'Ese es nuestro estándar mínimo'
  },
  booking: {
    title: 'Reserva tu experiencia',
    address: 'Dirección',
    phone: 'Teléfono',
    instagram: 'Instagram',
    cta: 'Reservar ahora'
  },
  footer: {
    tagline: 'Exclusive Barbershop · Marbella',
    quote: 'Marbella te observa. Sé la versión que mereces ser.'
  },
  langLabel: 'Seleccionar idioma'
};
const en: Copy = {
  nav: {
    experience: 'Experience',
    services: 'Services',
    houseCall: 'House Call',
    hours: 'Hours',
    book: 'Booking',
    bookShort: 'Book'
  },
  hero: {
    eyebrow: 'Exclusive Barbershop · Marbella',
    quote: '“Your image speaks before you open your mouth. Make sure it says the right thing.”',
    ctaPrimary: 'Book your experience',
    ctaSecondary: 'Call now'
  },
  truth: {
    title: 'The truth no one has told you',
    lines: ["You've paid well. You've tried several places. And you still walk out with something that's fine — but never perfect.", "The problem isn't the barbers. The problem is that no place was ever designed for men with your standards."],
    kicker: 'UNTIL NOW.'
  },
  nextLevel: {
    eyebrow: 'Next level',
    title: 'Welcome to another level',
    body: 'DuvayBarberVIP is not just another barbershop in Marbella. It is the only place where international precision technique, absolute attention and a genuinely luxurious setting come together in a single experience.',
    pillars: ['No waiting', 'No improvising'],
    lead: 'Every visit is a session designed for one thing only:',
    punch: 'That you walk out feeling invincible.'
  },
  trust: {
    eyebrow: 'Trust',
    headline: 'IF THEY TRUSTED US, YOU COULD BE NEXT',
    imageAlt: 'Professional client at DuvayBarberVIP with a signed jersey'
  },
  services: {
    eyebrow: 'The menu',
    title: 'What we offer',
    book: 'Book'
  },
  houseCall: {
    title: 'House Call Service',
    subtitle: 'Private barbers and exclusive attention',
    places: ['Villas', 'Hotels', 'Yachts', 'Private events'],
    punch: 'The same level. On your ground.',
    videoText: 'Luxury and VIP experience'
  },
  hours: {
    eyebrow: 'Hours',
    title: 'When we are open',
    rows: [{
      day: 'Mon – Fri',
      time: '10:00–14:00 · 16:00–21:00'
    }, {
      day: 'Saturday',
      time: '10:00–14:00 · 15:00–18:00'
    }, {
      day: 'Sunday',
      time: 'Closed'
    }]
  },
  guarantee: {
    title: 'One single guarantee',
    body: "If the result isn't exactly what you expected, we fix it. No questions. No excuses.",
    kicker: 'That is our minimum standard'
  },
  booking: {
    title: 'Book your experience',
    address: 'Address',
    phone: 'Phone',
    instagram: 'Instagram',
    cta: 'Book now'
  },
  footer: {
    tagline: 'Exclusive Barbershop · Marbella',
    quote: 'Marbella is watching. Be the version you deserve to be.'
  },
  langLabel: 'Select language'
};
const dictionaries: Record<Lang, Copy> = {
  es,
  en
};
type LanguageContextValue = {
  lang: Lang;
  setLang: (lang: Lang) => void;
  toggleLang: () => void;
  t: Copy;
};
const LanguageContext = createContext<LanguageContextValue | null>(null);
export function LanguageProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [lang, setLang] = useState<Lang>('es');
  const toggleLang = useCallback(() => {
    setLang(current => current === 'es' ? 'en' : 'es');
  }, []);
  const value = useMemo<LanguageContextValue>(() => ({
    lang,
    setLang,
    toggleLang,
    t: dictionaries[lang]
  }), [lang, toggleLang]);
  return <LanguageContext.Provider value={value} data-id="element-227" data-mp="3ckdn57pc1fve">{children}</LanguageContext.Provider>;
}
export function useLanguage(): LanguageContextValue {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return ctx;
}